import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  FDBox, FDSecondaryActionButton, FDStack, FDTooltip,
} from '@facet-wealth/design';
import { dataAttrs } from 'Helpers/Testing';
import fr from 'facet-rest';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import {
  PathComparisonPopoverButton,
} from 'Views/Page/Content/Templates/roadmapExplorerPageTemplate/pathComparisonPopoverButton';
import {
  SecondaryActionButtonRel,
} from 'Packages/FacetCore/Experimental/RelationshipComponents/SecondaryActionButtonRel';
import { useRoadmapPaths } from 'Components/UI/guidedHouseholdReview/pathsContext';
import { GuidedHouseholdReviewZoneDescriptions } from 'Components/UI/guidedHouseholdReview/zoneDescriptions';
import useEventTrackingLogEvent, { categories, eventTypes, eventSubTypes } from 'EventTracking/useEventTrackingLogEvent';
import { PrimaryActionButtonRel } from 'Packages/FacetCore/Experimental/RelationshipComponents/PrimaryActionButtonRel';
import NewRoadmapExplorerPathDialog from './newPath';

export const PathActions = ({ showGoals }) => {
  const {
    resource: roadmapExplorerResource, pathResource: selectedPathResource, setNewlyCreatedPathID,
  } = useRoadmapPaths();
  const { logStandardizedEvent } = useEventTrackingLogEvent({
    nodeName: 'PM1 or PM2 path actions',
  });

  const [newPathDialogOpen, setNewPathDialogOpen] = useState(false);
  const { refreshPageContentCollection } = usePageContentContext();

  const canCreateNewPath = selectedPathResource.relationship(fr.relationships.newPath.name) || selectedPathResource.relationship(fr.relationships.facetPlanDuplicate.name);
  let createPathButton = (
    <FDSecondaryActionButton
      disabled={showGoals || !canCreateNewPath}
      icon={'insertDriveFile'}
      iconPosition="end"
      onClick={() => setNewPathDialogOpen(true)}
      label="New path"
      {...dataAttrs('roadmap-explorer-new-path-btn')}
      size={'sm'}
    />
  );
  if (!canCreateNewPath) {
    createPathButton = (
      <FDTooltip title="You have reached the maximum number of paths. Delete or modify another path to continue." placement="top">
        <FDBox>
          {createPathButton}
        </FDBox>
      </FDTooltip>
    );
  }

  const selectedPath = () => {
    logStandardizedEvent(categories.pm1OrPM2, eventTypes.selectPath, eventSubTypes.success);
  };
  const selectOrExitButton = selectedPathResource?.withRelationship(fr.relationships.exit.name,
    (rel) => (
      <PrimaryActionButtonRel
        relationship={rel}
        {...dataAttrs('roadmap-explorer-update-path-btn')}
        afterSuccessfulOnClick={selectedPath}
        size="sm"
      />
    ),
    () => (
      <PrimaryActionButtonRel
        relationship={selectedPathResource?.relationship(fr.relationships.select.name)}
        {...dataAttrs('roadmap-explorer-select-path-btn')}
        afterSuccessfulOnClick={selectedPath}
        size="sm"
      />
    ));

  return (
    <FDStack direction="column">
      {showGoals ? <GuidedHouseholdReviewZoneDescriptions resource={roadmapExplorerResource} /> : null}
      <FDStack paddingY={2} />{/* Add padding to the top but not the bottom (there's already plenty of padding at the bottom) */}
      <FDStack direction={'row'} spacing={4} alignItems={'center'} justifyContent={'center'}>
        <FDStack direction={'row'} spacing={1}>
          <SecondaryActionButtonRel
            relationship={roadmapExplorerResource.relationship(fr.relationships.managePaths.name)}
            {...dataAttrs('roadmap-explorer-manage-paths-btn')}
            size="sm"
            disabled={showGoals}
          />
          <PathComparisonPopoverButton disabled={showGoals} />
          {createPathButton}
          {selectOrExitButton}
        </FDStack>
        <NewRoadmapExplorerPathDialog
          isOpen={newPathDialogOpen}
          closeModal={() => setNewPathDialogOpen(false)}
          onCreated={(id) => {
            setNewlyCreatedPathID(id);
            refreshPageContentCollection();
          }}
        />
      </FDStack>
    </FDStack>
  );
};

PathActions.propTypes = {
  showGoals: PropTypes.bool.isRequired,
};
